import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import { customStylesSm, DropdownIndicator } from '~/Styles/reactSelectStyles';
import CardMobile from './CardMobile';
import { formatCurrency, formatDate } from '../utils';

export default function ParcelasCondicionantes(props) {
  var intermediariasConfig = props.intermediariasConfig || [];
  var valorVenda = props.valorVenda || 0;
  var taxaPI = props.taxaPI || 0;
  var saldoFinanciarBase = props.saldoFinanciarBase || 0;
  var calcularVF = props.calcularValorFuturo;
  var dataVenda = props.dataVenda;
  var onChange = props.onChange;
  var onLinhasAplicadas = props.onLinhasAplicadas;
  var bloqueado = !!props.bloqueado;
  var mensagemBloqueio = props.mensagemBloqueio || 'Seção bloqueada.';
  var resetSignal = props.resetSignal;

  var [selecionada, setSelecionada] = useState(null);
  var [construcaoAplicada, setConstrucaoAplicada] = useState(false);
  var [adimplenciaAplicada, setAdimplenciaAplicada] = useState(false);

  // Reset signal do pai: quando o pai zera condicionantesAplicadas (troca de VV,
  // aplica CE, remove CE, recarrega dados etc), limpa o estado local pra não
  // ficar "fantasma" (checkbox marcado mas linha não persistida no pai).
  useEffect(function() {
    setSelecionada(null);
    setConstrucaoAplicada(false);
    setAdimplenciaAplicada(false);
  }, [resetSignal]);

  var selectStyles = useMemo(function() {
    return Object.assign({}, customStylesSm, {
      menuPortal: function(base) {
        return Object.assign({}, base, { zIndex: 99999 });
      },
    });
  }, []);

  var opcoes = useMemo(
    function() {
      return intermediariasConfig.map(function(item) {
        return {
          value: item.codigo,
          label: item.descricao || 'Intermediária ' + item.codigo,
          item: item,
        };
      });
    },
    [intermediariasConfig]
  );

  var projecao = useMemo(
    function() {
      if (!selecionada) return { construcao: null, adimplencia: null };

      var item = selecionada.item;
      var base = dataVenda ? new Date(dataVenda) : new Date();
      var hoje = new Date();
      var construcao = null;
      var adimplencia = null;

      var pctC = Number(item.percentual || 0);
      if (pctC > 0) {
        var mesesC = Number(item.meses || 0);
        var vencC = new Date(base.getFullYear(), base.getMonth() + mesesC, 20);
        var vpC = Math.round(valorVenda * (pctC / 100) * 100) / 100;
        var vfC = calcularVF ? calcularVF(vpC, taxaPI, base, vencC) : vpC;
        construcao = {
          tipo: 'Construção',
          meses: mesesC,
          percentual: pctC,
          valorPresente: vpC,
          valorFuturo: Math.round(vfC * 100) / 100,
          vencimento: vencC,
          codigoIntermediaria: selecionada.value,
        };
      }

      var pctA = Number(item.adimplencia_percentual || 0);
      if (pctA > 0) {
        var mesesA = Number(item.adimplencia_meses || 0);
        var vencA = new Date(hoje.getFullYear(), hoje.getMonth() + mesesA, 20);
        var vpA = Math.round(valorVenda * (pctA / 100) * 100) / 100;
        var vfA = calcularVF ? calcularVF(vpA, taxaPI, base, vencA) : vpA;
        adimplencia = {
          tipo: 'Adimplência',
          meses: mesesA,
          percentual: pctA,
          valorPresente: vpA,
          valorFuturo: Math.round(vfA * 100) / 100,
          vencimento: vencA,
          codigoIntermediaria: selecionada.value,
        };
      }

      return { construcao: construcao, adimplencia: adimplencia };
    },
    [selecionada, valorVenda, taxaPI, dataVenda, calcularVF]
  );

  var deducaoTotal = useMemo(
    function() {
      var total = 0;
      if (construcaoAplicada && projecao.construcao) {
        total += projecao.construcao.valorPresente;
      }
      if (adimplenciaAplicada && projecao.adimplencia) {
        total += projecao.adimplencia.valorPresente;
      }
      return Math.round(total * 100) / 100;
    },
    [construcaoAplicada, adimplenciaAplicada, projecao]
  );

  var saldoRestante = useMemo(
    function() {
      var s = saldoFinanciarBase - deducaoTotal;
      return Math.round(Math.max(s, 0) * 100) / 100;
    },
    [saldoFinanciarBase, deducaoTotal]
  );

  useEffect(
    function() {
      if (onChange) onChange(deducaoTotal);
    },
    [deducaoTotal, onChange]
  );

  useEffect(
    function() {
      if (!onLinhasAplicadas) return;
      var linhas = [];
      if (construcaoAplicada && projecao.construcao) linhas.push(projecao.construcao);
      if (adimplenciaAplicada && projecao.adimplencia) linhas.push(projecao.adimplencia);
      onLinhasAplicadas(linhas);
    },
    [construcaoAplicada, adimplenciaAplicada, projecao, onLinhasAplicadas]
  );

  var handleSelecionarIntermediaria = useCallback(function(option) {
    setSelecionada(option);
    setConstrucaoAplicada(false);
    setAdimplenciaAplicada(false);
  }, []);

  if (intermediariasConfig.length === 0) return null;

  if (bloqueado) {
    return (
      <Card className="mb-2">
        <Card.Body className="py-2">
          <h6 className="mb-1 text-primary">
            <strong>Parcelas Condicionantes</strong>
          </h6>
          <div
            className="rounded p-2"
            style={{ background: '#fff3cd', border: '1px solid #ffc107' }}
          >
            <small>{mensagemBloqueio}</small>
          </div>
        </Card.Body>
      </Card>
    );
  }

  var renderLinhaDesktop = function(dados, aplicada, setAplicada) {
    if (!dados) return null;
    return (
      <Row className="align-items-center mb-2 d-none d-md-flex">
        <Col md={1}>
          <Form.Check
            type="checkbox"
            checked={aplicada}
            onChange={function(e) {
              setAplicada(e.target.checked);
            }}
            id={'cond-desk-' + dados.tipo}
          />
        </Col>
        <Col md={2}>
          <small className="text-muted d-block">Tipo</small>
          <strong style={{ opacity: aplicada ? 1 : 0.5 }}>{dados.tipo}</strong>
        </Col>
        <Col md={2}>
          <small className="text-muted d-block">Valor Presente</small>
          <strong
            className={aplicada ? 'text-danger' : ''}
            style={{ opacity: aplicada ? 1 : 0.5 }}
          >
            R$ {formatCurrency(dados.valorPresente)}
          </strong>
        </Col>
        <Col md={2}>
          <small className="text-muted d-block">Valor Futuro</small>
          <span style={{ opacity: aplicada ? 1 : 0.5 }}>
            R$ {formatCurrency(dados.valorFuturo)}
          </span>
        </Col>
        <Col md={2}>
          <small className="text-muted d-block">Vencimento</small>
          <span style={{ opacity: aplicada ? 1 : 0.5 }}>
            {formatDate(dados.vencimento)}
          </span>
        </Col>
        <Col md={2}>
          <small className="text-muted d-block">Percentual</small>
          <span style={{ opacity: aplicada ? 1 : 0.5 }}>
            {formatCurrency(dados.percentual)}%
          </span>
        </Col>
      </Row>
    );
  };

  var renderLinhaMobile = function(dados, aplicada, setAplicada) {
    if (!dados) return null;
    return (
      <div className="d-block d-md-none mb-2">
        <div
          className="d-flex align-items-start"
          style={{
            border: '1px solid ' + (aplicada ? '#dc3545' : '#f0f0f0'),
            borderRadius: 6,
            padding: 12,
            boxShadow: '0 1px 2px rgba(44,62,80,0.04)',
            opacity: aplicada ? 1 : 0.6,
          }}
        >
          <Form.Check
            type="checkbox"
            checked={aplicada}
            onChange={function(e) {
              setAplicada(e.target.checked);
            }}
            id={'cond-mob-' + dados.tipo}
            className="mr-3 mt-1"
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                color: '#293c66',
                fontWeight: 600,
                fontSize: '0.95rem',
                marginBottom: 6,
              }}
            >
              {dados.tipo}
            </div>
            <Row>
              <Col xs={6} className="mb-1">
                <div style={{ color: '#7b8ca6', fontSize: '0.7rem' }}>
                  Valor Presente
                </div>
                <strong
                  className={aplicada ? 'text-danger' : ''}
                  style={{ fontSize: '0.85rem' }}
                >
                  R$ {formatCurrency(dados.valorPresente)}
                </strong>
              </Col>
              <Col xs={6} className="mb-1">
                <div style={{ color: '#7b8ca6', fontSize: '0.7rem' }}>
                  Valor Futuro
                </div>
                <span style={{ fontSize: '0.85rem' }}>
                  R$ {formatCurrency(dados.valorFuturo)}
                </span>
              </Col>
              <Col xs={6}>
                <div style={{ color: '#7b8ca6', fontSize: '0.7rem' }}>
                  Vencimento
                </div>
                <span style={{ fontSize: '0.85rem' }}>
                  {formatDate(dados.vencimento)}
                </span>
              </Col>
              <Col xs={6}>
                <div style={{ color: '#7b8ca6', fontSize: '0.7rem' }}>
                  Percentual
                </div>
                <span style={{ fontSize: '0.85rem' }}>
                  {formatCurrency(dados.percentual)}%
                </span>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card className="mb-2">
      <Card.Body className="py-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0 text-primary">
            <strong>Parcelas Condicionantes</strong>
          </h6>
          {taxaPI > 0 && (
            <small className="text-muted">
              Taxa PI: {formatCurrency(taxaPI)}% a.m.
            </small>
          )}
        </div>

        <Row className="mb-2">
          <Col md={6} xs={12}>
            <Form.Label className="small mb-1">
              Condição de Pagamento
            </Form.Label>
            <Select
              placeholder="Selecione uma condição..."
              options={opcoes}
              value={selecionada}
              onChange={handleSelecionarIntermediaria}
              styles={selectStyles}
              components={{ DropdownIndicator: DropdownIndicator }}
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </Col>
        </Row>

        {selecionada && deducaoTotal > 0 && (
          <div
            className="rounded p-2 mb-2"
            style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}
          >
            <Row className="align-items-center text-center">
              <Col xs={4}>
                <small className="text-muted d-block">Saldo</small>
                <span>R$ {formatCurrency(saldoFinanciarBase)}</span>
              </Col>
              <Col xs={4}>
                <small className="text-muted d-block">Condicionantes</small>
                <span className="text-danger">
                  - R$ {formatCurrency(deducaoTotal)}
                </span>
              </Col>
              <Col xs={4}>
                <small className="text-muted d-block">Restante</small>
                <strong className="text-success">
                  R$ {formatCurrency(saldoRestante)}
                </strong>
              </Col>
            </Row>
          </div>
        )}

        {selecionada && !projecao.construcao && !projecao.adimplencia && (
          <p className="text-muted mb-0">
            <small>
              Esta condição não possui percentuais configurados para construção
              nem adimplência.
            </small>
          </p>
        )}

        {renderLinhaDesktop(projecao.construcao, construcaoAplicada, setConstrucaoAplicada)}
        {renderLinhaDesktop(projecao.adimplencia, adimplenciaAplicada, setAdimplenciaAplicada)}
        {renderLinhaMobile(projecao.construcao, construcaoAplicada, setConstrucaoAplicada)}
        {renderLinhaMobile(projecao.adimplencia, adimplenciaAplicada, setAdimplenciaAplicada)}
      </Card.Body>
    </Card>
  );
}
